import { Decimal, Ulid } from "@kase/my-marathon-communication";
import { PatchLike, SpecialTypeLike } from "./interfaces";
import { isIDecimal, isIUlid } from "./type";

// supports the following possible field types: number, bigint, string, IUlid, IDecimal
//
const isEqual = <T>(a: T, b: SpecialTypeLike<T>) => {
    if (
        typeof a === "string" ||
        typeof a === "number" ||
        typeof a === "bigint" ||
        typeof a === "boolean"
    ) {
        return a === b;
    }

    if (isIUlid(a) && isIUlid(b)) {
        return Ulid.fromLoHi(a).equals(Ulid.fromLoHi(b));
    }

    if (isIDecimal(a) && isIDecimal(b)) {
        return new Decimal(a).equals(new Decimal(b));
    }

    if (typeof a === "undefined") {
        return typeof b === "undefined";
    }

    throw new Error("[notify developer]: Unsupported type for comparison");
};

export const isEqualObject = <T extends object>(a: T, b: T) => {
    const aKeys = Object.keys(a) as (keyof T)[];
    const bKeys = Object.keys(b) as (keyof T)[];

    if (aKeys.length !== bKeys.length) {
        return false;
    }

    for (const key of aKeys) {
        if (!isEqual(a[key], b[key] as SpecialTypeLike<T[keyof T]>)) {
            return false;
        }
    }

    return true;
};

export const hasPatchChanges = <T, OmitKeys extends keyof T = never>(
    record: T,
    params: PatchLike<T, OmitKeys>,
    write = false,
) => {
    let changed = false;

    for (const key in params) {
        const value = params[key as keyof typeof params];
        const recordValue = record[key as keyof T];

        if (value === undefined || value === null) {
            continue;
        }

        if (!isEqual(recordValue, value)) {
            changed = true;

            if (write) {
                record[key as keyof T] = value as T[keyof T];
            }
        }
    }

    return changed;
};

export const isSameDomain = (a: string, b: string) =>
    a.replace(/\.kase\.se$/, "") === b.replace(/\.kase\.se$/, "");
